/**
 * <format-picker>: the design's format menu (variant 1d) on top of the native <select name="id">.
 * The select stays in the form as the source of truth (and as the no-JS fallback); picking an
 * option sets it and fires `change`, which cart.js already uses to update the card price.
 */
class FormatPicker extends HTMLElement {
  connectedCallback() {
    if (this.button) return;
    this.select = this.querySelector('select');
    if (!this.select) return;

    const uid = `fp-${Math.random().toString(36).slice(2, 9)}`;
    const label = this.dataset.label || '';
    const chevron = this.querySelector('.format__chevron');

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'format format__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', uid);
    this.button.innerHTML = `<span class="format__label">${label}</span><span class="format__value"></span>`;
    if (chevron) this.button.append(chevron.cloneNode(true));

    this.list = document.createElement('ul');
    this.list.className = 'format__list';
    this.list.id = uid;
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-label', this.dataset.title || label);
    this.list.tabIndex = -1;
    this.list.hidden = true;

    this.options = [...this.select.options].map((option, index) => {
      const item = document.createElement('li');
      item.id = `${uid}-${index}`;
      item.className = 'format__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      if (option.disabled) item.setAttribute('aria-disabled', 'true');
      item.innerHTML = '<span class="format__name"><span class="format__ring" aria-hidden="true"></span></span><span class="format__price"></span>';
      item.querySelector('.format__name').append(option.dataset.label || option.text);
      item.querySelector('.format__price').textContent = option.dataset.price || '';
      this.list.append(item);
      return item;
    });

    this.append(this.button, this.list);
    this.classList.add('is-enhanced');
    this.sync();

    this.button.addEventListener('click', () => (this.list.hidden ? this.open() : this.close()));
    this.button.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      }
    });
    this.list.addEventListener('click', (event) => {
      const item = event.target.closest('[role="option"]');
      if (item) this.pick(item);
    });
    this.list.addEventListener('keydown', (event) => this.onKey(event));
    this.list.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close(false);
    });
  }

  sync() {
    const current = this.select.selectedIndex;
    this.options.forEach((item, index) => item.setAttribute('aria-selected', String(index === current)));
    const option = this.select.options[current];
    this.button.querySelector('.format__value').textContent = option ? option.dataset.label || option.text : '';
  }

  open() {
    document.querySelectorAll('format-picker').forEach((picker) => picker !== this && picker.close(false));
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.closest('[data-product-card]')?.classList.add('is-picking');
    this.activate(Math.max(0, this.select.selectedIndex));
    this.list.focus();
    this.outside = (event) => {
      if (!this.contains(event.target)) this.close(false);
    };
    document.addEventListener('pointerdown', this.outside);
  }

  close(focusButton = true) {
    if (this.list.hidden) return;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.closest('[data-product-card]')?.classList.remove('is-picking');
    document.removeEventListener('pointerdown', this.outside);
    if (focusButton) this.button.focus();
  }

  activate(index) {
    this.active = index;
    this.options.forEach((item, i) => item.classList.toggle('is-active', i === index));
    const item = this.options[index];
    if (!item) return;
    this.list.setAttribute('aria-activedescendant', item.id);
    item.scrollIntoView({ block: 'nearest' });
  }

  pick(item) {
    if (item.getAttribute('aria-disabled') === 'true') return;
    this.select.value = item.dataset.value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.sync();
    this.close();
  }

  onKey(event) {
    const last = this.options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activate(Math.min(last, this.active + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activate(Math.max(0, this.active - 1));
        break;
      case 'Home':
        event.preventDefault();
        this.activate(0);
        break;
      case 'End':
        event.preventDefault();
        this.activate(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.pick(this.options[this.active]);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close(false);
        break;
      default:
    }
  }
}

if (!customElements.get('format-picker')) customElements.define('format-picker', FormatPicker);
